/**
 * Embedded-editor URL params (lg/wid/hig/un/he/nextBtnCaption) are only re-emitted when usable — never as
 * null/undefined, blank, or the literal "null"/"undefined" text that already-indexed URLs carry.
 * Mirrors EmbeddedEditorParams::isUsableEmbeddedParamValue() on the PHP side; exposed on PMW.util for the
 * legacy assets/javascript tree (embedded-util.js).
 */
export const isUsableEmbeddedParamValue = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmedLowercaseValue = value.trim().toLowerCase();
  return trimmedLowercaseValue !== '' && trimmedLowercaseValue !== 'null' && trimmedLowercaseValue !== 'undefined';
};

/** Mirrors EmbeddedEditorParams::isEmbeddedRequest(): only the literal "1" is embedded, so a stray lg cannot propagate. */
export const isEmbeddedParamValue = (value: unknown): boolean => {
  return isUsableEmbeddedParamValue(value) && trimmedParamValue(value) === '1';
};

/** PHP's trim() also strips NUL and vertical tab; both sides deliberately trim only these four. */
const trimmedParamValue = (value: unknown): string => {
  return String(value).replace(/^[ \t\n\r]+|[ \t\n\r]+$/g, '');
};

/** A zero or non-numeric size is not a dimension, and a partial triple is meaningless to the receiving controller. */
export const hasUsableCustomDimensions = (width: unknown, height: unknown, units: unknown): boolean => {
  return isUsablePositiveDimension(width) && isUsablePositiveDimension(height) && isUsableEmbeddedParamValue(units);
};

/** Keep identical to EmbeddedEditorParams::POSITIVE_DIMENSION_PATTERN; the parity test pins them. */
const DECIMAL_DIMENSION = /^\d*\.?\d+$/;

const isUsablePositiveDimension = (value: unknown): boolean => {
  const dimension = trimmedParamValue(value);

  return isUsableEmbeddedParamValue(value) && DECIMAL_DIMENSION.test(dimension) && Number(dimension) > 0;
};
